import { FeatureFlag } from '@ps/web-core-enums';
import {
  Input,
  StarsButton,
  RawToggle as Toggle,
  Translation,
  required
} from '@ps/web-shared-components';
import { StarsInfo, createForm } from '@ps/web-shared-ui';
import type { FormApi } from 'final-form';
import { get } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import ButtonGroup from '@swf/components/ButtonGroup/ButtonGroup';
import { ModalContext } from '@swf/components/StarsModal/Context';
import Body from '@swf/components/StarsModal/components/Body';
import useFeature from '@swf/core/hooks/useFeature';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import { select } from '@swf/core/store';
import { minLength } from '@swf/core/utils/validators';
import { ServerMessageHandler } from '@swf/notification';
import {
  PKR_RESTRICTED_SYMBOLS,
  PKR_RESTRICTED_SYMBOLS_STRING,
  mustNotContainSymbols,
  useWidgetTheme
} from '@swf/poker';

import Header from '../../Modal/Header/Header';

import {
  TXTCLI_Club_Name,
  TXTCLI_Create_a_Poker_Club,
  TXTCLI_Create_a_club,
  TXTCLI_Invitation_Code,
  TXTCLI_Must_be_at_least_N0_characters_ELL,
  TXTCLI_Private,
  TXTCLI_XbXDo_not_use_your_PokerStars__ELL,
  TXTCLI_Your_identity_to_all_club_memb_ELL,
  TXTWCL_Keep_your_games_exclusive_with_ELL,
  TXTWCL_The_following_characters_are_i_ELL
} from './CreateClub.messages.json';
import styles from './CreateClub.scss';
import InfoIcon from './assets/info.svg';
import useCreateClub from './hooks/useCreateClub';

export interface FormValues {
  clubName: string;
  invitationCode: string;
  agreeTerms: string;
  isPrivate?: boolean;
}

const Form = createForm<FormValues>();

interface SearchParams {
  isPublic?: boolean;
}

export const CreateClub: React.FC = () => {
  const state = useSearchParams<SearchParams>();
  const isPublic = get(state, 'isPublic');

  const userId = useSelector(select.account.getUserId);
  const [isPrivate, setIsPrivate] = useState(true);
  const isPublicClubEnabled = useFeature(FeatureFlag.PKRHomeGamesPublicClubs);
  const isPublicClubJoinAndCreateEnabled = useFeature(
    FeatureFlag.PKRHomeGamesJoinAndCreatePublicClubs
  );

  const { formatMessage } = useIntl();
  const { response, handleSubmit, loading } = useCreateClub();
  const { error } = response;
  const clubNameText = formatMessage(TXTCLI_Club_Name);
  const invitationCodeText = formatMessage(TXTCLI_Invitation_Code);
  const { setStyle } = useContext(ModalContext);
  const { className } = useWidgetTheme();

  useEffect(() => {
    if (typeof isPublic !== 'undefined') {
      setIsPrivate(!isPublic);
    }
  }, [isPublic]);

  useEffect(() => {
    setStyle({
      overlayClassName: className,
      className: styles.createClubModal,
      closeOnOverlayClick: true,
      closeOnEsc: true
    });
  }, [className, setStyle]);

  const toggleHandler = ({ change }: FormApi<FormValues>) => {
    change('invitationCode', '');
    setIsPrivate(!isPrivate);
  };

  return (
    <>
      <Header title={<Translation {...TXTCLI_Create_a_Poker_Club} />} />
      <Body className={styles.body}>
        <div className={styles.createClub} data-testid="create-club-modal">
          <Form
            className={styles.form}
            onSubmit={(params) => handleSubmit({ ...params, isPrivate })}
            render={({ form }) => {
              const { submitting, pristine, validating, valid } = form.getState();
              return (
                <>
                  <Input
                    className={styles.input}
                    id="clubName"
                    label={clubNameText}
                    showValidationTick={false}
                    placeholder={clubNameText}
                    maxLength={30}
                    validators={[
                      required(),
                      mustNotContainSymbols(PKR_RESTRICTED_SYMBOLS, {
                        ...TXTWCL_The_following_characters_are_i_ELL,
                        values: { 0: PKR_RESTRICTED_SYMBOLS_STRING }
                      }),
                      minLength(8, {
                        ...TXTCLI_Must_be_at_least_N0_characters_ELL,
                        values: { 0: 8 }
                      })
                    ]}
                  />
                  {isPublicClubEnabled && isPublicClubJoinAndCreateEnabled && (
                    <>
                      <div className={styles.toggleButtonContainer}>
                        <Toggle
                          hideLabel
                          label=""
                          onOffToggle
                          className={styles.toggleButtonCheckbox}
                          type="checkbox"
                          id="privacy-toggle"
                          checked={isPrivate}
                          onChange={() => toggleHandler(form)}
                        />
                        <span className={styles.toggleButtonSpan}>
                          <Translation {...TXTCLI_Private} />
                        </span>
                      </div>
                      <span className={styles.info}>
                        <Translation {...TXTWCL_Keep_your_games_exclusive_with_ELL} />
                      </span>
                    </>
                  )}
                  {isPrivate && (
                    <Input
                      className={styles.input}
                      id="invitationCode"
                      label={invitationCodeText}
                      showValidationTick={false}
                      placeholder={invitationCodeText}
                      maxLength={30}
                      validators={[
                        ...(isPrivate ? [required()] : []),
                        mustNotContainSymbols(PKR_RESTRICTED_SYMBOLS, {
                          ...TXTWCL_The_following_characters_are_i_ELL,
                          values: { 0: PKR_RESTRICTED_SYMBOLS_STRING }
                        }),
                        minLength(8, {
                          ...TXTCLI_Must_be_at_least_N0_characters_ELL,
                          values: { 0: 8 }
                        })
                      ]}
                    />
                  )}
                  {isPrivate && (
                    <StarsInfo className={styles.infoPanel}>
                      <div className={styles.infoPanelIcon}>
                        <InfoIcon />
                      </div>
                      <Translation tagName="p" {...TXTCLI_XbXDo_not_use_your_PokerStars__ELL} />
                    </StarsInfo>
                  )}
                  <div className={styles.yourIdentity}>
                    <Translation
                      values={{ 0: userId }}
                      {...TXTCLI_Your_identity_to_all_club_memb_ELL}
                    />
                  </div>
                  {error && (
                    <StarsInfo className={styles.errorPanel} type="error">
                      <ServerMessageHandler message={error} />
                    </StarsInfo>
                  )}
                  <ButtonGroup>
                    <StarsButton
                      id="createClubSubmit"
                      type="submit"
                      loading={submitting || loading}
                      disabled={!valid || validating || pristine}
                      translation={TXTCLI_Create_a_club}
                    />
                  </ButtonGroup>
                </>
              );
            }}
          />
        </div>
      </Body>
    </>
  );
};

export default CreateClub;
